"use client";

import { TourCarousel } from "@/types/dto/destinations-page/destination-page-types";
import {Box, SxProps} from "@mui/material";
import React from "react";
import TourCardCarousel from "@/components/carousel/tour-card-carousel/tour-card-carousel";
import { Tour } from "@/types/dto/tours/tour";
import {BasicInfo, BasicInfoCta} from "@/types/dto/common/basic-info";
import DynamicCtaButton from "@/components/common/dynamic-cta-button/dynamic-cta-button";
import dynamicCtaButtonListFactory, {
  dynamicCtaButtonFactory
} from "@/factories/common/dynamic-cta-button/dynamic-cta-button-factory";
import useScrollToAnchor from "@/hooks/scroll-to-anchor/use-scroll-to-anchor";
import { SectionHeader } from "./section-header";

interface FeaturedToursSlotProps {
  header?: {
    sx?: SxProps;
  };
  copy?: {
    sx?: SxProps;
  }
}

interface FeaturedToursProps {
  tourCarousel: TourCarousel;
  showRating?: boolean;
  openRequestAQuote?: (tour: Tour) => void;
  moduleRankingPosition?: string;
  parentContainerType?: string;
  dataRankingAlgorithm?: string;
  padding?: {
    mobile?: string;
    tablet?: string;
    desktop?: string;
    wide?: string;
  };
  slotProps?: FeaturedToursSlotProps;
}

const FeaturedTours = ({
  tourCarousel,
  showRating,
  openRequestAQuote,
  moduleRankingPosition = "8",
  parentContainerType,
  dataRankingAlgorithm = "manual",
  padding,
  slotProps
}: FeaturedToursProps) => {
  const { tours, basicInfo, anchorId } = tourCarousel;

  // cta logic
  const listOfCtas: BasicInfoCta[] | undefined =
    "ctas" in basicInfo && basicInfo.ctas
      ? basicInfo.ctas
      : undefined;

  const singleCtaBasicInfo: BasicInfo | undefined =
    !("ctas" in basicInfo) || !basicInfo.ctas
      ? basicInfo
      : undefined;

  const singleDynamicCta = singleCtaBasicInfo?.callToActionText
    ? dynamicCtaButtonFactory(singleCtaBasicInfo)
    : null;

  const multipleDynamicCtas = listOfCtas?.length
    ? dynamicCtaButtonListFactory(listOfCtas)
    : null;

  useScrollToAnchor(anchorId);

  return (
    <Box
      sx={{
        padding: {
          xs: padding?.mobile ?? "4rem 2.4rem",
          sm: padding?.mobile ?? "4rem 3.2rem",
          md: padding?.tablet ?? "4rem 3.2rem",
          lg: padding?.desktop ?? "4rem 10rem",
          xl: padding?.wide ?? "4rem 10rem"
        }
      }}
      data-track="visibility"
      data-parent-container-type={parentContainerType || "carousel"}
      data-module-name="Tour Carousel Section"
      data-carousel-card-name="Featured Tours"
      data-carousel-position={moduleRankingPosition}
      data-ranking-algorithm={dataRankingAlgorithm}
      data-module-ranking-position={moduleRankingPosition}
      id={anchorId}
      component="section"
      data-testid="featured-tours"
      className="full-width-container"
    >
      <SectionHeader
        headerText={basicInfo.headerText}
        copyText={basicInfo.copyText?.replace(/<p>/g, "").replace(/<\/p>/g, "")}
        headerTestId="featured-tours-header"
        copyTestId="featured-tours-subheader"
        headerSx={{ textAlign: "center", textTransform: "none !important", ...slotProps?.header?.sx }}
        copySx={{ textAlign: "center", ...slotProps?.copy?.sx }}
        sx={{
          mb: "4rem"
        }}
      />
      <TourCardCarousel
        tours={tours}
        testId="featured-tours"
        showRating={showRating}
        openRequestAQuote={openRequestAQuote}
      />

      {(singleDynamicCta || multipleDynamicCtas) && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            alignItems: "center",
            justifyContent: "center",
            alignSelf: "stretch",
            flexShrink: 0,
            position: "relative",
            mt: 4,
            width: "100%"
          }}
        >
          {multipleDynamicCtas && multipleDynamicCtas.map((cta) => (
            <DynamicCtaButton
              key={"url" in cta.ctaData
                ? `${cta.ctaType}-${cta.ctaData.url}`
                : `${cta.ctaType}-${cta.label}`}
              label={cta.label ?? ""}
              ctaType={cta.ctaType}
              ctaData={cta.ctaData}
              testId="View-all-tours-button"
              variant="outlined"
              injectSource="featured-tours-carousel"
            />
          ))}
          {singleDynamicCta &&  (
            <DynamicCtaButton
              label={singleDynamicCta.label ?? ""}
              ctaType={singleDynamicCta.ctaType}
              ctaData={singleDynamicCta.ctaData}
              testId="View-all-tours-button"
              variant="outlined"
              injectSource="featured-tours-carousel"
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default FeaturedTours;
